import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import OnboardingModal from './OnboardingModal';
import SpotlightTour from './SpotlightTour';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProtectedRoute({ children, onUserLoaded }) {
  const [authState, setAuthState] = useState('checking');
  const [user, setUser] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const hasChecked = useRef(false);

  // Check for tour trigger from Settings
  useEffect(() => {
    if (location.pathname === '/' && authState === 'authenticated') {
      const triggerTour = localStorage.getItem('noyo_trigger_tour');
      if (triggerTour) {
        localStorage.removeItem('noyo_trigger_tour');
        // Small delay to let the page render
        setTimeout(() => setShowTour(true), 500);
      }
    }
  }, [location.pathname, authState]);

  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;

    const autoCompleteOnboardingForQRJoin = async (userData) => {
      // Apply twilight theme automatically
      document.documentElement.classList.remove('pretty', 'sunset');
      document.documentElement.classList.add('twilight', 'dark');
      localStorage.setItem('noyo_twilight', 'true');
      localStorage.setItem('noyo_dark_mode', 'true');
      localStorage.setItem('noyo_theme_picker_seen', 'true');
      localStorage.setItem('noyo_tour_completed', 'true');
      try {
        await axios.post(`${API}/onboarding/complete`, {
          selected_folders: ['Family', 'Travel', 'Work', 'Health', 'Food'],
          custom_folders: []
        });
      } catch (e) { /* ignore if onboarding API fails */ }
      const updatedUser = { ...userData, has_completed_onboarding: true };
      setUser(updatedUser);
      setAuthState('authenticated');
      if (onUserLoaded) onUserLoaded(updatedUser);
    };

    const needsQRJoinFastTrack = () => {
      return location.pathname.startsWith('/join/') || location.pathname.startsWith('/invite/') || localStorage.getItem('noyo_qr_join_folder_id') || localStorage.getItem('pending_invite_link');
    };

    const checkAuth = async () => {
      // DEV BYPASS: skip auth on localhost for preview (requires env var)
      if (process.env.REACT_APP_DEV_AUTH_BYPASS === 'true' && window.location.hostname === 'localhost') {
        const devUser = { user_id: 'dev-user', email: 'dev@localhost', name: 'Dev User', picture: null, has_completed_onboarding: true };
        setUser(devUser);
        setAuthState('authenticated');
        if (onUserLoaded) onUserLoaded(devUser);
        return;
      }

      if (location.state?.user) {
        const userData = location.state.user;
        window.history.replaceState({}, document.title);
        setUser(userData);
        setAuthState('authenticated');
        if (localStorage.getItem('noyo_pin_reset')) {
          try { await axios.post(`${API}/settings/reset-pin`); } catch (e) {}
          localStorage.removeItem('noyo_pin_reset');
        }
        if (!userData.has_completed_onboarding) {
          if (needsQRJoinFastTrack()) {
            await autoCompleteOnboardingForQRJoin(userData);
          } else {
            setShowOnboarding(true);
          }
        } else {
          // Show tour for users who completed onboarding but haven't seen the tour
          const tourCompleted = localStorage.getItem('noyo_tour_completed');
          if (!tourCompleted) {
            setTimeout(() => setShowTour(true), 500);
          }
          if (onUserLoaded) onUserLoaded(userData);
        }
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        if (response.data && response.data.user_id) {
          setUser(response.data);
          setAuthState('authenticated');
          if (localStorage.getItem('noyo_pin_reset')) {
            try { await axios.post(`${API}/settings/reset-pin`); } catch (e) {}
            localStorage.removeItem('noyo_pin_reset');
          }
          if (!response.data.has_completed_onboarding) {
            if (needsQRJoinFastTrack()) {
              await autoCompleteOnboardingForQRJoin(response.data);
            } else {
              setShowOnboarding(true);
            }
          } else {
            // Show tour for users who completed onboarding but haven't seen the tour
            const tourCompleted = localStorage.getItem('noyo_tour_completed');
            if (!tourCompleted) {
              setTimeout(() => setShowTour(true), 500);
            }
            if (onUserLoaded) onUserLoaded(response.data);
          }
        } else {
          setAuthState('unauthenticated');
        }
      } catch (error) {
        setAuthState('unauthenticated');
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (authState === 'unauthenticated') {
      // Save current path so we can return after login
      const currentPath = location.pathname + location.search;
      if (currentPath !== '/' && currentPath !== '/welcome') {
        localStorage.setItem('noyo_return_url', currentPath);
      }
      navigate('/welcome', { replace: true });
    }
  }, [authState, navigate, location]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    const updatedUser = { ...user, has_completed_onboarding: true };
    setUser(updatedUser);
    
    // If user came from QR join or invite link, skip the tour and go straight to the folder
    const qrFolderId = localStorage.getItem('noyo_qr_join_folder_id');
    const pendingInviteLink = localStorage.getItem('pending_invite_link');
    if (qrFolderId || pendingInviteLink) {
      localStorage.setItem('noyo_tour_completed', 'true');
      if (qrFolderId) {
        localStorage.removeItem('noyo_qr_join_folder_id');
        localStorage.removeItem('noyo_qr_join_folder_name');
        if (onUserLoaded) onUserLoaded(updatedUser);
        navigate(`/join/${qrFolderId}`, { replace: true });
      } else if (pendingInviteLink) {
        localStorage.removeItem('pending_invite_link');
        if (onUserLoaded) onUserLoaded(updatedUser);
        navigate(`/invite/${pendingInviteLink}`, { replace: true });
      }
      return;
    }

    // Show feature tour after onboarding if not already completed
    const tourCompleted = localStorage.getItem('noyo_tour_completed');
    if (!tourCompleted) {
      setShowTour(true);
    } else if (onUserLoaded) {
      onUserLoaded(updatedUser);
    }
  };

  const handleTourComplete = () => {
    setShowTour(false);
    if (onUserLoaded && user) {
      onUserLoaded(user);
    }
  };

  if (authState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (authState === 'unauthenticated' || !user) {
    return null;
  }

  if (showOnboarding) {
    return (
      <>
        {React.cloneElement(children, { user })}
        <OnboardingModal user={user} onComplete={handleOnboardingComplete} />
      </>
    );
  }

  return (
    <>
      {React.cloneElement(children, { user })}
      <SpotlightTour isOpen={showTour} onComplete={handleTourComplete} />
    </>
  );
}

export default ProtectedRoute;
